import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaEnvelope, FaPhone, FaGlobe } from 'react-icons/fa';
import logoSvg from '../../assets/images/logo.svg';
import Select from 'react-select';
import countryList from 'react-select-country-list';

interface CountryOption {
  label: string;
  value: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  country?: string;
  submit?: string;
}

export const PhoneNumberModal: React.FC = () => {
  const navigate = useNavigate();
  const { user, currentProfile, createProfile, updateUser } = useAuth();
  
  const storedUserData = localStorage.getItem('saycard_user_data') 
    ? JSON.parse(localStorage.getItem('saycard_user_data') || '{}') 
    : null;
  
  const [name, setName] = useState(storedUserData?.name || '');
  const [email, setEmail] = useState(storedUserData?.email || '');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<CountryOption | null>(null);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryOptions = React.useMemo(() => countryList().getData(), []) as CountryOption[];

  useEffect(() => {
    try {
      // Set from user object if available
      if (currentProfile?.name) setName(currentProfile.name);
      if (currentProfile?.email) setEmail(currentProfile.email);
      if (currentProfile?.phone) {
        console.log('Setting initial phone value:', currentProfile.phone);
        setPhone(currentProfile.phone);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, [user, currentProfile]);

  // Only show modal if user exists but has no profiles
  if (!user || (user.profiles && user.profiles.length > 0)) {
    return null;
  }

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!country) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    setIsSubmitting(true);
    try {
      const formData = {
        name,
        email,
        phone,
        country: country?.value
      };
  
      console.log('Creating first profile:', formData);
      const createdProfile = await createProfile({
        ...formData,
        friendlyName: 'Default',  // Set the friendly name to 'Default' for first profile
        name: formData.name      // Keep the name for vCard purposes
      });
      
      // Update user document with name and country
      await updateUser({
        name: formData.name,
        country: formData.country
      });
      
      // Update localStorage with the new profile data
      localStorage.setItem('saycard_user_data', JSON.stringify({
        name: formData.name,
        email: formData.email
      }));
      
      localStorage.removeItem('saycard_google_profile');
      
      // Only navigate if profile was created successfully
      if (createdProfile) {
        navigate('/');
      }
    } catch (error) {
      console.error('Profile creation error:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to create profile. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-center mb-6">
          <img src={logoSvg} alt="SayCard" className="w-[140px] h-auto" />
        </div>
        <h2 className="text-2xl font-bold text-center mb-6">Set up your first contact profile</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="flex items-center space-x-2 text-gray-700 mb-2">
              <FaUser />
              <span>Name <span className="text-red-500">*</span></span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your name"
              required
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-700 mb-2">
              <FaEnvelope />
              <span>Email <span className="text-red-500">*</span></span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your email"
              required
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-700 mb-2">
              <FaPhone />
              <span>Phone <span className="text-red-500">*</span></span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your phone number"
              required
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-700 mb-2">
              <FaGlobe />
              <span>Country <span className="text-red-500">*</span></span>
            </label>
            <Select
              options={countryOptions}
              value={country}
              onChange={(option) => setCountry(option as CountryOption)}
              className="w-full"
              classNamePrefix="select"
              placeholder="Select your country"
            />
            {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
          </div>

          {errors.submit && (
            <div className="text-red-500 text-center">{errors.submit}</div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Creating Profile...' : 'Create Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PhoneNumberModal;
